import { Component } from "@theme/component";
import { fetchConfig } from "@theme/utilities";
import { ThemeEvents, CartAddEvent, CartErrorEvent, VariantUpdateEvent } from "@theme/events";
import { cartPerformance } from "@theme/performance";

/**
 * A custom element that manages instant add to cart buttons (single + box).
 *
 * @typedef {object} InstantAddButtonsRefs
 * @property {HTMLFormElement} [singleForm] - The single item form.
 * @property {HTMLFormElement} [boxForm] - The box form.
 * @property {HTMLInputElement} [singleVariantId] - The single form variant ID input.
 * @property {HTMLInputElement} [boxVariantId] - The box form variant ID input.
 * @property {HTMLElement} liveRegion - The live region for screen reader announcements.
 * @property {import('./product-form').AddToCartComponent} [singleButtonContainer] - The single button container.
 * @property {import('./product-form').AddToCartComponent} [boxButtonContainer] - The box button container.
 *
 * @extends Component<InstantAddButtonsRefs>
 */
class InstantAddButtonsComponent extends Component {
  requiredRefs = ["liveRegion"];

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest(".shopify-section, dialog, product-card");
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });

    // Use event delegation to intercept all form submissions within the component.
    // This ensures forms are handled correctly regardless of when nested blocks are rendered.
    this.addEventListener("submit", this.#handleSubmit, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * Handles form submission for instant add buttons.
   * @param {SubmitEvent} event
   */
  #handleSubmit = async (event) => {
    event.preventDefault();

    const form = /** @type {HTMLFormElement} */ (event.target);
    const button = form.querySelector('button[type="submit"]');

    if (!form || button?.disabled) return;

    const formData = new FormData(form);

    // Get cart items sections
    const cartItemsComponents = document.querySelectorAll("cart-items-component");
    const cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    });
    if (cartItemComponentsSectionIds.length > 0) {
      formData.append("sections", cartItemComponentsSectionIds.join(","));
    }

    const fetchCfg = fetchConfig("javascript", { body: formData });

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        ...fetchCfg,
        headers: {
          ...fetchCfg.headers,
          Accept: "text/html",
        },
      });

      const data = await response.json();

      if (data.status) {
        // Error response
        this.dispatchEvent(
          new CartErrorEvent(form.getAttribute("id") || "", data.message, data.description, data.errors),
        );

        this.#setLiveRegionText(data.message);

        setTimeout(() => {
          this.#clearLiveRegionText();
        }, 5000);
      } else {
        // Success
        const id = formData.get("id");
        if (!id) throw new Error("Form ID is required");

        this.#setLiveRegionText(Theme.translations.added);

        setTimeout(() => {
          this.#clearLiveRegionText();
        }, 5000);

        // Trigger animations on the add-to-cart component
        const addToCartContainer = form.querySelector("add-to-cart-component");
        if (addToCartContainer) {
          this.#animateFlyToCart(addToCartContainer);
          addToCartContainer.animateAddToCart?.();
        }

        this.dispatchEvent(
          new CartAddEvent({}, id.toString(), {
            source: "instant-add-buttons-component",
            itemCount: Number(formData.get("quantity")) || 1,
            productId: this.dataset.productId,
            sections: data.sections,
          }),
        );
      }
    } catch (error) {
      console.error("Instant add to cart error:", error);
    } finally {
      cartPerformance.measureFromEvent("add:user-action", event);
    }
  };

  /**
   * Animates the fly to cart animation.
   * @param {HTMLElement} addToCartContainer - The add-to-cart-component element.
   */
  #animateFlyToCart(addToCartContainer) {
    const button = addToCartContainer.querySelector('button[type="submit"]');
    const cartIcon = document.querySelector(".header-actions__cart-icon");
    const image = addToCartContainer.dataset.productVariantMedia;
    const animationEnabled = addToCartContainer.dataset.addToCartAnimation === "true";

    if (!cartIcon || !button || !image || !animationEnabled) return;

    /** @type {HTMLElement & {source: Element, destination: Element}} */
    const flyToCartElement = /** @type {any} */ (document.createElement("fly-to-cart"));

    flyToCartElement.classList.add("fly-to-cart--main");
    flyToCartElement.style.setProperty("background-image", `url(${image})`);
    flyToCartElement.style.setProperty("--start-opacity", "0");
    flyToCartElement.source = button;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Updates variant IDs and button states when variant changes.
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const variant = event.detail.resource;

    // Update variant IDs
    if (this.refs.singleVariantId) {
      this.refs.singleVariantId.value = variant?.id?.toString() ?? "";
    }
    if (this.refs.boxVariantId) {
      this.refs.boxVariantId.value = variant?.id?.toString() ?? "";
    }

    // Update button states
    const isAvailable = variant?.available ?? false;

    const singleButton = this.refs.singleButtonContainer?.refs?.addToCartButton;
    if (singleButton) {
      singleButton.disabled = !isAvailable;
    }

    const boxButton = this.refs.boxButtonContainer?.refs?.addToCartButton;
    if (boxButton) {
      // For box, also check inventory
      const boxContents = parseInt(this.dataset.boxContents || "1");
      const inventoryQuantity = variant?.inventory_quantity ?? 0;
      const inventoryPolicy = variant?.inventory_policy ?? "deny";
      const inventoryManagement = variant?.inventory_management;

      let canAddBox = isAvailable;
      if (inventoryManagement && inventoryPolicy === "deny" && inventoryQuantity < boxContents) {
        canAddBox = false;
      }

      boxButton.disabled = !canAddBox;
    }

    // Update product variant media for animation
    if (variant?.featured_media?.preview_image?.src) {
      const mediaUrl = variant.featured_media.preview_image.src + "&width=100";
      this.refs.singleButtonContainer?.setAttribute("data-product-variant-media", mediaUrl);
      this.refs.boxButtonContainer?.setAttribute("data-product-variant-media", mediaUrl);
    }
  };

  /**
   * Sets the live region text for screen readers.
   * @param {string} text
   */
  #setLiveRegionText(text) {
    this.refs.liveRegion.textContent = text;
  }

  /**
   * Clears the live region text.
   */
  #clearLiveRegionText() {
    this.refs.liveRegion.textContent = "";
  }
}

if (!customElements.get("instant-add-buttons-component")) {
  customElements.define("instant-add-buttons-component", InstantAddButtonsComponent);
}

export { InstantAddButtonsComponent };
